import { HISTORY_NOTE_FIELDS } from "@/constants/clients/client-appointment-constants";
import { isoDate } from "@/lib/utils";
import { appointmentStatusLabel } from "@/constants/appointments/appointments-constants";
import type { ClientProfile } from "@/types/copilot/copilot-types";

import { CardSection, DataCard, ServiceLines } from "./data-card";
import { useIntl } from "react-intl";

export function ClientCard({ profile }: { profile: ClientProfile }) {
  const intl = useIntl();
  const { client, recent_visits: visits, upcoming } = profile;
  const summary = [
    `${client.first_name} ${client.last_name}`,
    visits[0] ? `last visit ${intl.formatDate(isoDate(visits[0].appointment_date), { format: "day" })}` : "no visits yet",
    upcoming[0] ? `next in ${intl.formatDate(isoDate(upcoming[0].appointment_date), { format: "day" })}` : "nothing booked",
  ].join(" · ");

  return (
    <DataCard summary={summary}>
      <p>
        {[client.phone, client.staff_name && `Prefers ${client.staff_name}`].filter(Boolean).join(" · ") || "—"}
      </p>
      {visits.length > 0 && (
        <CardSection title="Last visits">
          {visits.map((visit) => (
            <div key={visit.id} className="flex flex-col gap-0.5 border-l-2 pl-2">
              <p className="font-medium">{intl.formatDate(isoDate(visit.appointment_date), { format: "day" })}</p>
              <ServiceLines appointment={visit} />
              {visit.products.map((product) => (
                <p key={product.id}>
                  {product.product_name} · {intl.formatNumber(product.price, { format: "money" })}
                </p>
              ))}
              {HISTORY_NOTE_FIELDS.map(({ name, label }) =>
                visit[name] ? (
                  <p key={name} className="whitespace-pre-line">
                    <span className="text-muted-foreground">{label}: </span>
                    {visit[name]}
                  </p>
                ) : null
              )}
            </div>
          ))}
        </CardSection>
      )}
      {upcoming.length > 0 && (
        <CardSection title="Upcoming">
          {upcoming.map((appointment) => (
            <div key={appointment.id} className="flex flex-col gap-0.5 border-l-2 pl-2">
              <p className="font-medium">
                {intl.formatDate(isoDate(appointment.appointment_date), { format: "day" })} · {appointmentStatusLabel(appointment.status)}
              </p>
              <ServiceLines appointment={appointment} />
            </div>
          ))}
        </CardSection>
      )}
    </DataCard>
  );
}
